import React, { useEffect, useRef, useState, useCallback } from 'react';
import { scrollToSection } from '../utils/scroll';
import { useTypewriter } from '../hooks/useTypewriter';
import { useIsMobileOrTablet } from '../hooks/useIsMobileOrTablet';
import { ArrowUpRight, Copy, Check } from 'lucide-react';

export default function Hero() {
  const isMobileOrTablet = useIsMobileOrTablet();
  const videoRef = useRef(null);
  const containerRef = useRef(null);

  const prevXRef = useRef(null);
  const targetProgressRef = useRef(0);
  const smoothedProgressRef = useRef(0);

  const isSeekingRef = useRef(false);
  const pendingSeekRef = useRef(null);

  const [actionsVisible, setActionsVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  const typewriterText =
    'Crafting high-performance mobile apps and scalable web architectures.';
  const { displayed, done } = useTypewriter(typewriterText, 38, 600);

  // 1. Priming: Pause and pre-seek to frame 0.01 to ensure instant ready state & warm GPU buffer (PC / Laptop only)
  useEffect(() => {
    if (isMobileOrTablet) return;
    const video = videoRef.current;
    if (!video) return;

    video.pause();
    const primeVideo = () => {
      try {
        video.currentTime = 0.01;
      } catch {
        // ignore seek error on unprimed media
      }
    };

    if (video.readyState >= 1) {
      primeVideo();
    } else {
      video.addEventListener('loadedmetadata', primeVideo, { once: true });
    }
  }, [isMobileOrTablet]);

  // 2. Perform seek using queue to prevent browser flooding
  const performSeek = useCallback((targetTime) => {
    const video = videoRef.current;
    if (!video || !video.duration || isNaN(video.duration)) return;

    const clamped = Math.max(0, Math.min(video.duration - 0.05, targetTime));
    if (Math.abs(video.currentTime - clamped) < 0.02) return;

    if (!isSeekingRef.current) {
      isSeekingRef.current = true;
      video.currentTime = clamped;
    } else {
      pendingSeekRef.current = clamped;
    }
  }, []);

  // 3. Render loop: Smooth lerp interpolation & 60fps scrubbing (PC / Laptop only)
  useEffect(() => {
    if (isMobileOrTablet) return;
    let animationFrameId;

    const renderLoop = () => {
      smoothedProgressRef.current +=
        (targetProgressRef.current - smoothedProgressRef.current) * 0.12;
      const progress = smoothedProgressRef.current;

      const video = videoRef.current;
      if (video && video.duration && !isNaN(video.duration)) {
        const targetTime = progress * Math.max(0, video.duration - 0.05);
        performSeek(targetTime);
      }

      animationFrameId = requestAnimationFrame(renderLoop);
    };

    animationFrameId = requestAnimationFrame(renderLoop);
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [performSeek, isMobileOrTablet]);

  // 4. Mouse and touch event listeners for horizontal scrubbing (PC / Laptop only)
  useEffect(() => {
    if (isMobileOrTablet) return;
    const video = videoRef.current;

    const handleSeeked = () => {
      isSeekingRef.current = false;
      if (pendingSeekRef.current !== null) {
        const nextTime = pendingSeekRef.current;
        pendingSeekRef.current = null;
        if (videoRef.current) {
          isSeekingRef.current = true;
          videoRef.current.currentTime = nextTime;
        }
      }
    };

    if (video) {
      video.addEventListener('seeked', handleSeeked);
    }

    const SENSITIVITY = 0.85;

    const handleMouseMove = (e) => {
      const currentX = e.clientX;
      if (prevXRef.current === null) {
        prevXRef.current = currentX;
        return;
      }

      const delta = currentX - prevXRef.current;
      prevXRef.current = currentX;

      const offset = (delta / (window.innerWidth || 1)) * SENSITIVITY;
      targetProgressRef.current = Math.max(0, Math.min(1, targetProgressRef.current + offset));
    };

    const handleTouchMove = (e) => {
      if (e.touches && e.touches.length > 0) {
        const currentX = e.touches[0].clientX;
        if (prevXRef.current === null) {
          prevXRef.current = currentX;
          return;
        }

        const delta = currentX - prevXRef.current;
        prevXRef.current = currentX;

        const offset = (delta / (window.innerWidth || 1)) * SENSITIVITY;
        targetProgressRef.current = Math.max(0, Math.min(1, targetProgressRef.current + offset));
      }
    };

    const handleInteractionEnd = () => {
      prevXRef.current = null;
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleInteractionEnd, { passive: true });
    window.addEventListener('mouseup', handleInteractionEnd, { passive: true });

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleInteractionEnd);
      window.removeEventListener('mouseup', handleInteractionEnd);
      if (video) {
        video.removeEventListener('seeked', handleSeeked);
      }
    };
  }, [isMobileOrTablet]);

  // Action buttons appearance after 400ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setActionsVisible(true);
    }, 400);
    return () => clearTimeout(timer);
  }, []);

  const handleCopyEmail = useCallback(async () => {
    try {
      await navigator.clipboard.writeText('lokeswaraninfo@gmail.com');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.warn('Clipboard copy failed:', err);
    }
  }, []);

  const pills = [
    { label: 'View My Work', target: 'projects', primary: true },
  ];

  return (
    <section
      id="hero"
      ref={containerRef}
      className="relative w-full min-h-screen supports-[height:100svh]:min-h-[100svh] overflow-hidden bg-black flex flex-col font-cinematic-body text-white"
      aria-label="Portfolio Hero"
    >
      {/* Mainframe Hero Media Background:
          - PC or Laptop (>= 1024px): mainframe-hero.mp4 (video background with smooth scrubbing)
          - Mobile & Tablets ("taps") (< 1024px): mainframe-hero.png (static image background)
          100% full opacity, zero fade, zero blur
      */}
      <div className="absolute inset-0 w-full h-full pointer-events-none select-none z-0 overflow-hidden">
        {/* Mobile & Tablets ("taps") (< 1024px) */}
        <img
          src="/mainframe-hero.png"
          alt="Mainframe Hero"
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-center block lg:hidden"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            opacity: 1, // Full 100% opacity, zero fade
          }}
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />

        {/* PC or Laptop (>= 1024px) */}
        <video
          ref={videoRef}
          src="/mainframe-hero.mp4"
          muted
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover hidden lg:block"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 1, // Full 100% opacity, zero fade
          }}
          aria-hidden="true"
        />
      </div>

      {/* Main Hero Content Area */}
      <div className="relative z-20 flex-1 flex flex-col justify-end pb-12 md:justify-center md:pb-0 px-5 sm:px-8 md:px-12 pt-24 pointer-events-auto">
        <div className="max-w-3xl">
          {/* Role Badge & Name */}
          <div className="mb-4 sm:mb-6">
            <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-black/45 backdrop-blur-xl border border-white/20 mb-4 sm:mb-5 shadow-[0_4px_24px_rgba(0,0,0,0.5)]">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400 shadow-[0_0_10px_#34d399]" />
              </span>
              <span className="font-mono text-[11px] sm:text-xs font-semibold uppercase tracking-[0.22em] text-white/90">
                React Native &amp; Full Stack Developer
              </span>
            </div>

            <h1 className="font-sans text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.08] drop-shadow-[0_6px_28px_rgba(0,0,0,0.9)] select-none">
              Lokeswaran M
            </h1>
          </div>

          {/* Typewriter Text with blinking cursor */}
          <div className="min-h-[3.6em] text-[17px] sm:text-[21px] lg:text-[23px] leading-relaxed text-white/90 font-sans font-normal max-w-xl drop-shadow-[0_2px_16px_rgba(0,0,0,0.9)]">
            <span>{displayed}</span>
            {!done && (
              <span
                className="inline-block w-[2.5px] h-[1.15em] bg-cyan-400 ml-1.5 align-middle rounded-full animate-cursor-blink shadow-[0_0_10px_#22d3ee]"
                aria-hidden="true"
              />
            )}
          </div>

          {/* Action Pills & Email */}
          <div
            style={{
              transition: 'opacity 0.4s cubic-bezier(0.16, 1, 0.3, 1), transform 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
            className={`flex flex-col items-start gap-3 mt-7 select-none ${
              actionsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
            }`}
          >
            {/* Primary & Secondary Nav Pills */}
            <div className="flex flex-wrap items-center gap-2.5 sm:gap-3">
              {pills.map((pill) =>
                pill.primary ? (
                  <button
                    key={pill.label}
                    type="button"
                    onClick={() => scrollToSection(pill.target, { behavior: 'smooth' })}
                    className="inline-flex items-center justify-center gap-2 bg-white text-dark-950 hover:bg-neutral-100 font-semibold text-xs sm:text-sm px-5 sm:px-6 py-2.5 rounded-full shadow-[0_4px_20px_rgba(0,0,0,0.35)] hover:shadow-[0_6px_28px_rgba(255,255,255,0.35)] hover:scale-105 active:scale-95 transition-all duration-200 cursor-pointer group whitespace-nowrap"
                  >
                    <span>{pill.label}</span>
                    <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </button>
                ) : (
                  <button
                    key={pill.label}
                    type="button"
                    onClick={() => scrollToSection(pill.target, { behavior: 'smooth' })}
                    className="inline-flex items-center justify-center bg-black/45 hover:bg-white/20 text-white/90 hover:text-white border border-white/25 hover:border-white/50 backdrop-blur-xl font-medium text-xs sm:text-sm px-4 sm:px-5 py-2.5 rounded-full shadow-md hover:scale-105 active:scale-95 transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    {pill.label}
                  </button>
                )
              )}
            </div>

            {/* Email Pill */}
            <button
              type="button"
              onClick={handleCopyEmail}
              className="inline-flex items-center justify-center gap-2.5 bg-black/55 hover:bg-black/75 backdrop-blur-xl border border-white/25 hover:border-cyan-400/60 text-white font-medium text-xs sm:text-sm px-4 sm:px-5 py-2 rounded-full shadow-[0_4px_20px_rgba(0,0,0,0.4)] hover:shadow-[0_0_24px_rgba(6,182,212,0.35)] hover:scale-105 active:scale-95 transition-all duration-200 cursor-pointer group whitespace-nowrap"
              title="Click to copy email address"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span className="text-emerald-300 font-semibold">Copied to clipboard!</span>
                </>
              ) : (
                <>
                  <span className="text-white/70 text-xs uppercase tracking-wider font-mono">Email:</span>
                  <span className="text-cyan-300 font-mono text-xs sm:text-sm tracking-tight group-hover:text-cyan-200 transition-colors">
                    lokeswaraninfo@gmail.com
                  </span>
                  <Copy className="w-3.5 h-3.5 text-white/60 group-hover:text-cyan-300 transition-colors shrink-0 ml-0.5" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}